import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, CheckCircle, AlertTriangle, ChevronDown, ChevronRight, Globe, Database, Search, Wifi, User, Mail, Building2, KeyRound, Clock, Link2, ListPlus } from 'lucide-react';
import { differenceInDays, format } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../components/ui/Toast';
import { ApiResponse, RegistrarAccount } from '../types';
import PageHeader from '../components/ui/PageHeader';
import { Skeleton, SkeletonCard } from '../components/ui/Skeleton';

interface ExistingAccount {
  id: string;
  label: string;
  registrar_type: string;
  registrar_name: string;
  has_api: number;
  has_credentials: number;
  synced_count: number;
}

interface MissingAccount {
  registrant_name: string | null;
  registrant_email: string | null;
  registrant_organization: string | null;
  domain_count: number;
  domains: { id: string; domain_name: string; expiry_date: string | null; status: string }[];
}

interface RegistrarGroup {
  whois_registrar: string;
  whois_registrar_url: string | null;
  matched_registrar_type: string | null;
  existing_accounts: ExistingAccount[];
  missing_accounts: MissingAccount[];
  total_uncovered: number;
}

interface ManualAnalysis {
  summary: {
    total: number;
    csv_count: number;
    manual_count: number;
    has_whois: number;
    missing_whois: number;
    covered: number;
    uncovered: number;
    expired: number;
  };
  groups: RegistrarGroup[];
}

export default function ManualDomainsPage() {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [analysis, setAnalysis] = useState<ManualAnalysis | null>(null);
  const [accounts, setAccounts] = useState<RegistrarAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Per-missing-account-row state: which account dropdown selection + in-flight key
  const [groupSelections, setGroupSelections] = useState<Record<string, string>>({});
  const [assigning, setAssigning] = useState<string | null>(null);
  const [assignResults, setAssignResults] = useState<Record<string, { assigned: number; not_found: string[] }>>({});

  // Paste-and-link tool state
  const [pasteText, setPasteText] = useState('');
  const [pasteAccountId, setPasteAccountId] = useState('');
  const [pasteSubmitting, setPasteSubmitting] = useState(false);
  const [pasteResult, setPasteResult] = useState<{ assigned: number; not_found: string[]; account_label: string } | null>(null);

  useEffect(() => {
    loadData();
    loadAccounts();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const res = await fetchApi<ApiResponse<ManualAnalysis>>('/reports/manual-analysis');
      if (res.data) setAnalysis(res.data);
    } catch (error) {
      console.error('Failed to load analysis:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadAccounts() {
    try {
      const res = await fetchApi<ApiResponse<RegistrarAccount[]>>('/accounts');
      if (res.data) setAccounts(res.data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  }

  // Bulk-assign a single missing-account group's domain_ids to the chosen account.
  async function assignGroup(rowKey: string, accountId: string, domainIds: string[]) {
    if (!accountId || domainIds.length === 0) return;
    setAssigning(rowKey);
    try {
      const res = await fetchApi<ApiResponse<{ assigned: number; not_found: string[] }>>('/domains/bulk-assign', {
        method: 'POST',
        body: JSON.stringify({ account_id: accountId, domain_ids: domainIds }),
      });
      if (res.data) {
        setAssignResults(prev => ({ ...prev, [rowKey]: res.data! }));
      }
      // Reload analysis so the linked group disappears / count drops
      await loadData();
    } catch (error) {
      toast.error('Bulk assign failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setAssigning(null);
    }
  }

  // Parse the paste textarea: split on whitespace/commas/newlines, drop blanks, lowercase.
  function parseDomainList(text: string): string[] {
    return text
      .split(/[\s,;]+/)
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
  }

  async function submitPaste() {
    const names = parseDomainList(pasteText);
    if (names.length === 0 || !pasteAccountId) return;
    setPasteSubmitting(true);
    setPasteResult(null);
    try {
      const res = await fetchApi<ApiResponse<{ assigned: number; not_found: string[]; account_label: string }>>(
        '/domains/bulk-assign',
        {
          method: 'POST',
          body: JSON.stringify({ account_id: pasteAccountId, domain_names: names }),
        }
      );
      if (res.data) {
        setPasteResult(res.data);
        await loadData();
      }
    } catch (error) {
      toast.error('Bulk assign failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setPasteSubmitting(false);
    }
  }

  const toggleGroup = (registrar: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(registrar)) next.delete(registrar);
      else next.add(registrar);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-7 w-72" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} lines={2} />)}
        </div>
        <SkeletonCard lines={8} />
      </div>
    );
  }

  if (!analysis) return null;

  const { summary, groups } = analysis;
  const whoisPct = summary.total > 0 ? Math.round((summary.has_whois / summary.total) * 100) : 0;
  const coveredPct = summary.total > 0 ? Math.round((summary.covered / summary.total) * 100) : 0;
  const totalMissingAccounts = groups.reduce((sum, g) => sum + g.missing_accounts.length, 0);

  return (
    <div>
      <div className="mb-6">
        <PageHeader
          title="Manual Domains Analysis"
          description="Identify missing registrar accounts and set up API access"
          actions={
            <>
              <button onClick={loadData} disabled={loading} className="btn btn-secondary text-sm">
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <Link to="/domains" className="btn btn-secondary text-sm">
                <Globe className="h-4 w-4 mr-2" />
                All Domains
              </Link>
            </>
          }
        />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <SummaryCard
          title="Manual/CSV Domains"
          value={summary.total}
          subtitle={`${summary.manual_count} manual, ${summary.csv_count} CSV`}
          icon={Database}
          color="brand"
        />
        <SummaryCard
          title="WHOIS Data"
          value={summary.has_whois}
          subtitle={`${whoisPct}% coverage`}
          icon={Search}
          color={whoisPct >= 80 ? 'emerald' : whoisPct >= 50 ? 'amber' : 'rose'}
        />
        <SummaryCard
          title="Covered"
          value={summary.covered}
          subtitle="Assigned to an account"
          icon={CheckCircle}
          color="emerald"
        />
        <SummaryCard
          title="Uncovered"
          value={summary.uncovered}
          subtitle="Missing account access"
          icon={AlertTriangle}
          color={summary.uncovered === 0 ? 'emerald' : 'rose'}
        />
        <SummaryCard
          title="Expired"
          value={summary.expired}
          subtitle={`${summary.total > 0 ? Math.round((summary.expired / summary.total) * 100) : 0}% of total`}
          icon={Clock}
          color={summary.expired === 0 ? 'emerald' : 'rose'}
        />
        <SummaryCard
          title="Missing Accounts"
          value={totalMissingAccounts}
          subtitle="Need 1Password setup"
          icon={KeyRound}
          color={totalMissingAccounts === 0 ? 'emerald' : 'amber'}
        />
      </div>

      {/* Progress bar */}
      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-700">Account Coverage</span>
          <span className="text-sm text-slate-500">
            {summary.covered} of {summary.total} domains assigned to a connected account
          </span>
        </div>
        <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${coveredPct >= 80 ? 'bg-emerald-500' : coveredPct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
            style={{ width: `${coveredPct}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-slate-400">{coveredPct}% covered</span>
          <span className="text-xs text-slate-400">{summary.uncovered} uncovered across {groups.length} registrar{groups.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Missing WHOIS notice */}
      {summary.missing_whois > 0 && (
        <div className="card p-4 mb-6 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                {summary.missing_whois} domain{summary.missing_whois !== 1 ? 's' : ''} missing WHOIS data
              </p>
              <p className="text-xs text-amber-600 mt-0.5">
                WHOIS lookups run hourly. Domains without WHOIS data cannot be grouped by registrar.
              </p>
            </div>
            <Link to="/whois" className="btn btn-secondary text-xs py-1 px-3">
              View WHOIS
            </Link>
          </div>
        </div>
      )}

      {/* Paste-and-link bulk tool */}
      <div className="card p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ListPlus className="h-4 w-4 text-brand-600" />
          <h2 className="text-base font-semibold text-slate-900">Bulk-link domains by name</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Paste a list of domain names (one per line, or comma-separated) and pick a registrar account.
          All matching domains in your inventory will be reassigned to that account.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <textarea
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            placeholder={'example.com\nanother.org\n…'}
            spellCheck={false}
            rows={6}
            className="md:col-span-2 w-full px-3 py-2 text-sm border border-slate-300 rounded-md font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            disabled={pasteSubmitting}
          />
          <div className="flex flex-col gap-2">
            <label className="block text-xs font-medium text-slate-600">Target account</label>
            <select
              value={pasteAccountId}
              onChange={e => setPasteAccountId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              disabled={pasteSubmitting}
            >
              <option value="">— Select an account —</option>
              {accounts
                .slice()
                .sort((a, b) =>
                  a.registrar_name.localeCompare(b.registrar_name) ||
                  a.label.localeCompare(b.label)
                )
                .map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.registrar_name} — {acc.label}
                  </option>
                ))}
            </select>
            <button
              onClick={submitPaste}
              disabled={pasteSubmitting || !pasteAccountId || parseDomainList(pasteText).length === 0}
              className="btn btn-primary mt-auto"
            >
              {pasteSubmitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Link {parseDomainList(pasteText).length || ''} domain{parseDomainList(pasteText).length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
        {pasteResult && (
          <div className={`mt-4 p-3 rounded-md text-sm ${pasteResult.assigned > 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
            <div className="flex items-start gap-2">
              {pasteResult.assigned > 0 ? (
                <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="font-medium">
                  Linked {pasteResult.assigned} domain{pasteResult.assigned === 1 ? '' : 's'} to {pasteResult.account_label}
                </p>
                {pasteResult.not_found.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs">
                      {pasteResult.not_found.length} not found in inventory
                    </summary>
                    <pre className="mt-2 text-xs font-mono bg-surface/50 p-2 rounded max-h-40 overflow-auto">
                      {pasteResult.not_found.join('\n')}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Uncovered registrar groups */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-slate-900">
          Uncovered Domains by Registrar
        </h2>
        <p className="text-sm text-slate-500 -mt-1 mb-2">
          These domains are not assigned to any connected account. Each group below likely represents a registrar account that needs to be added to 1Password and configured with API access.
        </p>

        {groups.map((group) => {
          const isExpanded = expandedGroups.has(group.whois_registrar);
          const hasExisting = group.existing_accounts.length > 0;
          const isNoWhois = group.whois_registrar === '[No WHOIS Data]';

          return (
            <div key={group.whois_registrar} className="card overflow-hidden">
              {/* Group header */}
              <div
                className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleGroup(group.whois_registrar)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                )}

                {isNoWhois ? (
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-300 flex-shrink-0" />
                ) : (
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">
                      {group.whois_registrar}
                    </span>
                    <span className="text-sm text-slate-500">
                      ({group.total_uncovered} domain{group.total_uncovered !== 1 ? 's' : ''})
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Show count of distinct missing accounts */}
                  {!isNoWhois && group.missing_accounts.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                      {group.missing_accounts.length} missing account{group.missing_accounts.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {hasExisting && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {group.existing_accounts.length} connected
                    </span>
                  )}
                  {isNoWhois && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      <Search className="h-3 w-3 mr-1" />
                      Needs WHOIS
                    </span>
                  )}
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div className="border-t border-slate-200">
                  {/* Existing connected accounts for this registrar */}
                  {hasExisting && (
                    <div className="px-5 py-3 bg-emerald-50/50 border-b border-slate-200">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                        Connected Accounts ({group.existing_accounts.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {group.existing_accounts.map(a => (
                          <span
                            key={a.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs bg-surface border border-emerald-200 text-slate-700"
                          >
                            <Wifi className="h-3 w-3 text-emerald-500" />
                            <span className="font-medium">{a.label}</span>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-500">{a.synced_count} synced</span>
                            {a.has_credentials ? (
                              <CheckCircle className="h-3 w-3 text-emerald-500" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 text-amber-500" />
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Missing accounts */}
                  <div className="px-5 py-4 space-y-4">
                    {group.missing_accounts.map((ma, idx) => {
                      const rowKey = `${group.whois_registrar}::${idx}`;
                      const selectedAccountId = groupSelections[rowKey] || '';
                      const result = assignResults[rowKey];
                      const isAssigning = assigning === rowKey;
                      // Prefer accounts of the matched registrar type. If none, allow selecting any.
                      const assignableAccounts = group.existing_accounts.length > 0
                        ? group.existing_accounts.map(a => ({
                            id: a.id,
                            label: a.label,
                            registrar_name: a.registrar_name,
                          }))
                        : accounts
                            .filter(a =>
                              !group.matched_registrar_type ||
                              a.registrar_type === group.matched_registrar_type
                            )
                            .map(a => ({
                              id: a.id,
                              label: a.label,
                              registrar_name: a.registrar_name,
                            }));
                      const domainIds = ma.domains.map(d => d.id);

                      return (
                      <div key={idx} className="p-3 bg-surface rounded-lg border border-slate-200">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                              Missing Account #{idx + 1}
                            </p>
                            <div className="space-y-1">
                              {ma.registrant_email && (
                                <div className="flex items-center gap-1.5 text-sm">
                                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                                  <span className="text-slate-700 font-medium">{ma.registrant_email}</span>
                                </div>
                              )}
                              {ma.registrant_name && (
                                <div className="flex items-center gap-1.5 text-sm">
                                  <User className="h-3.5 w-3.5 text-slate-400" />
                                  <span className="text-slate-600">{ma.registrant_name}</span>
                                </div>
                              )}
                              {ma.registrant_organization && (
                                <div className="flex items-center gap-1.5 text-sm">
                                  <Building2 className="h-3.5 w-3.5 text-slate-400" />
                                  <span className="text-slate-600">{ma.registrant_organization}</span>
                                </div>
                              )}
                              {!ma.registrant_email && !ma.registrant_name && !ma.registrant_organization && (
                                <p className="text-sm text-slate-400 italic">No registrant info available</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-sm font-bold text-slate-900">
                              {ma.domain_count} domain{ma.domain_count !== 1 ? 's' : ''}
                            </span>
                            {(() => {
                              const expired = ma.domains.filter(d => d.expiry_date && new Date(d.expiry_date) < new Date()).length;
                              const active = ma.domain_count - expired;
                              return expired > 0 ? (
                                <div className="text-xs mt-0.5">
                                  <span className="text-emerald-600">{active} active</span>
                                  {' · '}
                                  <span className="text-red-600">{expired} expired</span>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </div>

                        <div className="space-y-0.5">
                          {ma.domains.map(d => {
                            const isExpired = d.expiry_date && new Date(d.expiry_date) < new Date();
                            const daysLeft = d.expiry_date ? differenceInDays(new Date(d.expiry_date), new Date()) : null;
                            return (
                              <div key={d.id} className="flex items-center gap-2 group">
                                <Link
                                  to={`/domains/${d.id}`}
                                  className={`text-sm hover:underline truncate ${isExpired ? 'text-red-500 hover:text-red-700' : 'text-brand-600 hover:text-brand-800'}`}
                                >
                                  {d.domain_name}
                                </Link>
                                {d.expiry_date && (
                                  <span className={`text-xs whitespace-nowrap flex-shrink-0 ${
                                    isExpired ? 'text-red-500' : daysLeft !== null && daysLeft <= 30 ? 'text-amber-600' : 'text-slate-400'
                                  }`}>
                                    {isExpired
                                      ? `expired ${format(new Date(d.expiry_date), 'MMM d, yyyy')}`
                                      : format(new Date(d.expiry_date), 'MMM d, yyyy')}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Inline link-to-account action */}
                        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                          <select
                            value={selectedAccountId}
                            onChange={e => setGroupSelections(prev => ({ ...prev, [rowKey]: e.target.value }))}
                            className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            disabled={isAssigning || assignableAccounts.length === 0}
                          >
                            <option value="">
                              {assignableAccounts.length === 0
                                ? `No ${group.matched_registrar_type || 'matching'} accounts configured yet`
                                : '— Link these domains to account —'}
                            </option>
                            {assignableAccounts.map(a => (
                              <option key={a.id} value={a.id}>
                                {a.registrar_name} — {a.label}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => assignGroup(rowKey, selectedAccountId, domainIds)}
                            disabled={!selectedAccountId || isAssigning || domainIds.length === 0}
                            className="btn btn-primary text-sm"
                          >
                            {isAssigning ? (
                              <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <Link2 className="h-3.5 w-3.5 mr-1.5" />
                            )}
                            Link {ma.domain_count}
                          </button>
                        </div>
                        {result && (
                          <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 p-2 rounded">
                            Linked {result.assigned} domain{result.assigned === 1 ? '' : 's'}
                            {result.not_found.length > 0 && ` · ${result.not_found.length} not found`}
                          </div>
                        )}
                      </div>
                      );
                    })}

                    {group.missing_accounts.length === 0 && isNoWhois && (
                      <p className="text-sm text-slate-500 italic">
                        Run WHOIS lookups to identify which registrar these domains belong to.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {groups.length === 0 && (
          <div className="card p-8 text-center">
            <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-slate-900 font-medium">All domains are covered</p>
            <p className="text-sm text-slate-500 mt-1">
              Every manually-entered domain is assigned to a connected registrar account.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'brand' | 'emerald' | 'amber' | 'rose';
}) {
  const colorClasses = {
    brand: 'bg-brand-100 text-brand-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-rose-100 text-rose-600',
  };

  return (
    <div className="card p-5">
      <div className="flex items-center gap-4">
        <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-400">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}
